import { io } from "socket.io-client";
import instance from "api/cmsapi";

export const DPAT_REALTIME_ROOM_EVENTS = [
  "dpat:invalidate",
  "comments-changed",
  "manual-score-changed",
  "assessment-started",
];

const VISIBLE_WATCH_MS = 20000;
const HIDDEN_WATCH_MS = 45000;
const INITIAL_WATCH_DELAY_MS = 20000;

let socket = null;
let activeRoom = null;
let activeUsername = "";
let invalidateHandler = null;
let lastJoinPayload = null;
let broadcastChannel = null;

function resolveSocketUrl() {
  const fromEnv = import.meta.env.VITE_DPAT_SOCKET_URL;
  if (fromEnv) return String(fromEnv).replace(/\/$/, "");
  try {
    return new URL(instance.defaults.baseURL, window.location.origin).origin;
  } catch {
    return undefined;
  }
}

export function getDpatRealtimeRoom(districtId, year) {
  if (!districtId || year == null || year === "") return null;
  return `dpat:${districtId}:${year}`;
}

function commentsRowsFromResponse(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

export function buildCommentsRevisionStamp(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return "empty";
  return list
    .map((row) =>
      [
        row?.id ?? "",
        row?.tableCommented ?? "",
        Array.isArray(row?.updateDate) ? row.updateDate.join("-") : row?.updateDate ?? "",
        Array.isArray(row?.commentDate) ? row.commentDate.join("-") : row?.commentDate ?? "",
      ].join("\u001f"),
    )
    .sort()
    .join("\u001e");
}

async function fetchCommentsRevisionStamp(districtId, year) {
  const response = await instance.get(`comments/tables/${districtId}/${year}/DPAT`);
  return buildCommentsRevisionStamp(commentsRowsFromResponse(response));
}

function isOwnEvent(payload) {
  if (!payload || typeof payload !== "object") return false;
  return Boolean(payload.senderId && socket?.id && payload.senderId === socket.id);
}

function matchesActiveRoom(payload) {
  if (!activeRoom) return false;
  if (!payload || typeof payload !== "object") return true;
  if (payload.room && payload.room !== activeRoom) return false;
  if (payload.districtId != null && payload.year != null) {
    return activeRoom === getDpatRealtimeRoom(payload.districtId, payload.year);
  }
  return true;
}

function handleRealtimeEvent(payload) {
  if (isOwnEvent(payload) || !matchesActiveRoom(payload)) return;
  invalidateHandler?.(payload);
}

function emitJoin() {
  if (!socket?.connected || !lastJoinPayload) return;
  socket.emit("dpat:join", lastJoinPayload);
}

function bindRealtimeListeners(nextSocket) {
  DPAT_REALTIME_ROOM_EVENTS.forEach((eventName) => {
    nextSocket.off(eventName);
    nextSocket.on(eventName, handleRealtimeEvent);
  });
  nextSocket.off("connect", emitJoin);
  nextSocket.on("connect", emitJoin);
}

function openBroadcastChannel(room) {
  if (typeof BroadcastChannel === "undefined" || !room) return null;
  const channel = new BroadcastChannel(room);
  channel.onmessage = (event) => {
    if (event?.data?.type !== "dpat:invalidate") return;
    invalidateHandler?.(event.data);
  };
  return channel;
}

export function connectDpatRealtime({ districtId, year, username, onInvalidate }) {
  const room = getDpatRealtimeRoom(districtId, year);
  const url = resolveSocketUrl();
  if (!room) {
    return () => {};
  }

  activeUsername = username || "";
  invalidateHandler = onInvalidate;
  broadcastChannel?.close();
  broadcastChannel = openBroadcastChannel(room);

  if (url) {
    if (!socket) {
      socket = io(url, {
        path: import.meta.env.VITE_DPAT_SOCKET_PATH || "/socket.io",
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 10,
        autoConnect: true,
      });
      bindRealtimeListeners(socket);
    } else if (!socket.connected) {
      socket.connect();
    }
  }

  if (activeRoom && activeRoom !== room) {
    socket?.emit("dpat:leave", { room: activeRoom });
  }

  activeRoom = room;
  lastJoinPayload = {
    room,
    districtId,
    year,
    type: "DPAT",
  };
  emitJoin();

  return () => {
    if (activeRoom === room) {
      disconnectDpatRealtime();
    }
  };
}

export function startDpatCommentsRevisionWatch({ districtId, year, onChange }) {
  if (!districtId || year == null || year === "") {
    return () => {};
  }

  let cancelled = false;
  let timer = null;
  let lastStamp = null;

  const tick = async () => {
    try {
      const stamp = await fetchCommentsRevisionStamp(districtId, year);
      if (cancelled) return;
      if (lastStamp == null) {
        lastStamp = stamp;
      } else if (stamp !== lastStamp) {
        lastStamp = stamp;
        onChange?.();
      }
    } catch {
      // Keep watching; a single failed GET should not stop live updates.
    }
    if (!cancelled) {
      timer = window.setTimeout(tick, document.hidden ? HIDDEN_WATCH_MS : VISIBLE_WATCH_MS);
    }
  };

  timer = window.setTimeout(tick, INITIAL_WATCH_DELAY_MS);

  return () => {
    cancelled = true;
    if (timer) window.clearTimeout(timer);
  };
}

export function emitDpatQueryInvalidation(extra = {}) {
  const payload = {
    room: activeRoom,
    senderId: socket?.id,
    username: activeUsername,
    type: "DPAT",
    ...extra,
  };
  if (socket?.connected && activeRoom) {
    socket.emit("dpat:invalidate", payload);
  }
  try {
    broadcastChannel?.postMessage({ type: "dpat:invalidate", ...payload });
  } catch {
    // Ignore closed-channel races during unmount.
  }
}

export function disconnectDpatRealtime() {
  if (activeRoom) {
    socket?.emit("dpat:leave", { room: activeRoom });
  }
  activeRoom = null;
  invalidateHandler = null;
  activeUsername = "";
  lastJoinPayload = null;
  try {
    broadcastChannel?.close();
  } catch {
    // already closed
  }
  broadcastChannel = null;
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
}
